import { FC, useEffect, useState } from "react";
import { Modal, Form, Button, Table, InputNumber, Input, notification, App, Tooltip, Space, Flex } from "antd";
import { DeleteOutlined, PlusOutlined } from "@ant-design/icons";
import { ReimbursRequest, AddDailyRequestItem, AddDailyRequest } from "@/types/reimburse";
import iconFolder from "@/assets/images/fileIcon/folder.svg";
import { useTranslation } from "react-i18next";
import { ProductSelectMulti } from "@/components/Selector";
import { addDaily, applicationSubmit } from "@/apis/reimburse";
import { ColumnsType } from "antd/es/table";
import TabDocTable, { OpenTabObj } from "@/components/TabDocTable";
import { AnyObject } from "antd/es/_util/type";
import { numFormmater } from "@/pages/Requirement/util";

interface DailySupplyItem extends AddDailyRequestItem {
  id: string;
}

interface AddDailySuppliesProps {
  open: boolean;
  onSuccess: (data: ReimbursRequest) => void;
  onClose: () => void;
}

const AddDailySupplies: FC<AddDailySuppliesProps> = ({
  open,
  onSuccess,
  onClose,
}) => {
  const { t } = useTranslation();
  const { modal } = App.useApp()
  const [form] = Form.useForm();
  const [loading, setLoading] = useState<"ADD" | "SUBMIT" | undefined>(undefined);
  const [openProductSelect, setOpenProductSelect] = useState(false);
  const [items, setItems] = useState<DailySupplyItem[]>([]);
  const [openTab, setOpenTab] = useState<{ index: number } & OpenTabObj | undefined>(undefined);

  useEffect(() => {
    if (open) {
      form.resetFields();
      setItems([]);
    }
  }, [open, form]);

  const handleSubmit = async (isSubmit: boolean) => {
    setLoading(isSubmit ? "SUBMIT" : "ADD");
    try {
      const values = await form.validateFields();

      // 验证物料列表
      const validItems = items.filter(item => item.count > 0);
      if (validItems.length === 0) {
        notification.error({
          placement: 'top',
          message: t("common.tip"),
          description: t("reimburse.plSelectProducts"),
        });
        setLoading(undefined);
        return;
      }

      // 转换为 AddDailyRequestItem 格式
      const requestItems: AddDailyRequestItem[] = validItems.map(item => ({
        productCode: item.productCode,
        productName: item.productName,
        productModel: item.productModel || "",
        count: item.count,
        desc: item.desc || "",
        docId: item.docId,
      }));

      const params: AddDailyRequest = {
        title: values.title,
        content: values.content || "",
        purpose: values.purpose || "",
        remark: values.remark || "",
        items: requestItems,
      };

      let result = await addDaily(params);
      if (isSubmit) result = await applicationSubmit(false, result.id)
      notification.success({
        placement: 'top',
        message: isSubmit ? t("reimburse.submitSuccess") : t("common.addSuccess"),
      });
      onSuccess(result);
      onClose();
    } catch (error: unknown) {
      console.error("Form submit error:", error);
      notification.error({
        placement: 'top',
        message: t("common.operationFailed"),
        description: t("common.pleaseTryAgain"),
      });
    } finally {
      setLoading(undefined);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    setItems([]);
    onClose();
  };

  const handleDeleteItem = (index: number) => {
    if (openTab && openTab.index === index) setOpenTab(undefined);
    setItems(prev => prev.filter((_, i) => i !== index));
  };

  const handleAddEmptyItem = () => {
    const newItem: DailySupplyItem = {
      id: "",
      productCode: "",
      productName: "",
      productModel: "",
      count: 1,
      desc: "",
      docId: "",
    };
    setItems(prev => [...prev, newItem]);
  };

  const handleUpdateItem = (index: number, field: keyof Omit<DailySupplyItem, 'id'>, value: string | number) => {
    setItems(prev => {
      const newItems = [...prev];
      newItems[index] = { ...newItems[index], [field]: value };
      return newItems;
    });
  };

  const columns: ColumnsType<DailySupplyItem> = [
    {
      title: t("purchase.seq"),
      width: 60,
      render: (_, __, index) => index + 1,
    },
    {
      title: t("product.code"),
      dataIndex: "productCode",
      width: 120,
      render: (value, record, index) => {
        if (record.id === "") {
          return (
            <Input
              value={value}
              onChange={(e) => handleUpdateItem(index, "productCode", e.target.value)}
              placeholder={t("product.code")}
            />
          );
        }
        return <span>{value || "-"}</span>;
      },
    },
    {
      title: t("product.name"),
      dataIndex: "productName",
      width: 150,
      render: (value, record, index) => {
        if (record.id === "") {
          return (
            <Input
              value={value}
              onChange={(e) => handleUpdateItem(index, "productName", e.target.value)}
              placeholder={t("product.name")}
            />
          );
        }
        return <span>{value || "-"}</span>;
      },
    },
    {
      title: t("product.brand1"),
      dataIndex: "productModel",
      width: 200,
      render: (value, record, index) => {
        if (record.id === "") {
          return (
            <Input
              value={value}
              onChange={(e) => handleUpdateItem(index, "productModel", e.target.value)}
              placeholder={t("product.brand1")}
            />
          );
        }
        return <span>{value || "-"}</span>;
      },
    },
    {
      title: t("reimburse.count"),
      dataIndex: "count",
      width: 120,
      render: (value, record, index) => (
        <InputNumber changeOnWheel
          formatter={val => numFormmater(Number(val || 0))}
          min={1}
          precision={0}
          value={value}
          onChange={(val) => handleUpdateItem(index, "count", val ?? 1)}
          style={{ width: "100%" }}
        />
      ),
    },
    {
      title: t("common.desc"),
      dataIndex: "desc",
      width: 200,
      render: (value, record, index) => (
        <Input.TextArea
          placeholder={t("components.plDictDesc")}
          autoSize={{ minRows: 1, maxRows: 1 }}
          maxLength={500}
          value={value}
          onChange={(e) => handleUpdateItem(index, "desc", e.target.value)}
          style={{ paddingBottom: 10 }}
        />
      ),
    },
    {
      title: t('common.attachment'),
      dataIndex: "relatedDocuments",
      fixed: "right",
      width: 60,
      render: (_, item, index) => (
        <Flex align="center" justify="center" style={{ width: "100%" }}>
          <img
            src={iconFolder}
            alt=""
            onClick={() => setOpenTab({ index, docId: item.docId || "", name: `${item.productName ? item.productName + '-' : ''} ${t('common.attachment')}` })}
            style={{ cursor: "pointer" }}
          />
        </Flex>
      ),
    },
    {
      title: t("common.opt"),
      width: 80,
      fixed: "right",
      render: (_, __, index) => (
        <Tooltip title={t("common.del")}>
          <Button
            type="text"
            danger
            icon={<DeleteOutlined />}
            onClick={() => {
              modal.confirm({
                title: t("common.rmTip"),
                content: t("common.rmContent"),
                onOk: () => handleDeleteItem(index),
                okText: t("common.confirm"),
                cancelText: t("common.cancel"),
              });
            }}
          />
        </Tooltip>
      ),
    },
  ];

  return (
    <>
      <Modal
        title={`${t("reimburse.addApplication")} ${t("reimburse.dailySupplies")}`}
        open={open}
        onCancel={handleCancel}
        maskClosable={false}
        destroyOnClose
        width={1200}
        footer={
          <>
            <Button onClick={handleCancel}>{t("common.cancel")}</Button>
            <Button disabled={loading && loading !== "ADD"} type="primary" onClick={() => handleSubmit(false)} loading={loading === "ADD"}>
              {t("common.save")}
            </Button>
            <Button disabled={loading && loading !== "SUBMIT"} type="link" onClick={() => handleSubmit(true)} loading={loading === "SUBMIT"}>
              {t("reimburse.submit")}
            </Button>
          </>
        }
      >
        <Form form={form} labelCol={{ style: { width: 80, textAlign: 'right' } }}>
          <Form.Item
            label={t("reimburse.title")}
            name="title"
            rules={[{ required: true, message: t("reimburse.plTitle") }]}
            style={{ marginBottom: 8 }}
          >
            <Input placeholder={t("reimburse.plTitle")} maxLength={200} />
          </Form.Item>
          <Form.Item
            label={t("reimburse.content")}
            name="content"
            rules={[{ required: true, message: t("reimburse.plContent") }]}
            style={{ marginBottom: 24 }}
          >
            <Input.TextArea
              placeholder={t("reimburse.plContent")}
              autoSize={{ minRows: 2, maxRows: 2 }}
              maxLength={500}
              showCount
            />
          </Form.Item>
          <Form.Item
            label={t("reimburse.purpose")}
            name="purpose"
            style={{ marginBottom: 8 }}
          >
            <Input placeholder={t("reimburse.plPurpose")} maxLength={200} />
          </Form.Item>
          <Form.Item label={t("reimburse.remark")} name="remark" style={{ marginBottom: 8 }}>
            <Input.TextArea
              placeholder={t("reimburse.plRemark")}
              autoSize={{ minRows: 2, maxRows: 2 }}
              maxLength={500}
              showCount
            />
          </Form.Item>
          <Form.Item label={t("reimburse.items")} style={{ marginBottom: 8 }}>
            <Space>
              <Button type="primary" onClick={() => setOpenProductSelect(true)} icon={<PlusOutlined />}>
                {t("reimburse.selectProducts")}
              </Button>
              <Button
                type="primary"
                icon={<PlusOutlined />}
                onClick={handleAddEmptyItem}
                style={{ marginLeft: 8 }}
              >
                {t("reimburse.addDailyItem")}
              </Button>
            </Space>
          </Form.Item>
        </Form>
        <TabDocTable
          tableName={t("reimburse.itemList")}
          openTab={openTab}
          tableProps={{
            columns: columns as AnyObject[],
            dataSource: items,
            pagination: false,
            scroll: { y: 400, x: "max-content" }
          }}
          docProps={{
            docId: openTab?.docId ?? '',
            forceReset: true,
            handleNewDocId(id) {
              openTab && setOpenTab({ ...openTab, docId: id });
              if (openTab?.index !== undefined) {
                handleUpdateItem(openTab.index, "docId", id);
              }
            },
          }}
          onClose={() => setOpenTab(undefined)}
        />

      </Modal>

      <ProductSelectMulti
        isQuantity

        handleOk={(products) => {
          const existingIds = items.map(item => item.id);
          const newItems = products
            .filter(p => !existingIds.includes(p.id))
            .map(p => ({
              id: p.id,
              productCode: p.code,
              productName: p.name,
              productModel: p.model || "",
              count: p.quantity || 1,
              desc: "",
              docId: "",
            }));
          setItems(prev => [...prev, ...newItems]);
        }}
        openProduct={openProductSelect}
        setOpenProduct={setOpenProductSelect}
      />
    </>
  );
};

export default AddDailySupplies;
